/**
 * Structured data (Schema.org) для SEO и для ИИ-ботов.
 * Домен берётся из NEXT_PUBLIC_SITE_URL, чтобы не хардкодить окружение.
 */

// Адрес берётся из модуля без зависимостей, а не из `dh-seo`: тот тянет
// навигацию next-intl, и разметку бренда нельзя было бы проверить
// юнит-тестом (#169).
import { DH_SITE_URL } from '@/lib/dh-site-url';
import { razmetkaKroshek } from '@/lib/kroshki';
// По той же причине адрес русской витрины берётся из `ais-ru-domen`:
// модуль без импортов вовсе, и разметку можно проверить юнит-тестом.
import { AIS_RU_SITE_URL } from '@/lib/ais-ru-domen';

/**
 * Адрес международной витрины AiSearch.
 *
 * Дублирует значение из `ais-seo.ts` намеренно: тот модуль тянет
 * навигацию next-intl, и импорт оттуда сделал бы разметку бренда
 * непроверяемой в тесте (ровно та же причина, что у `dh-site-url`).
 */
const AIS_SITE_URL = process.env.NEXT_PUBLIC_AISEARCH_URL ?? 'https://aisearch.tech';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://ingdg.com';
const ORG_NAME = 'INGDG';
const CONTACT_EMAIL = 'hello@ingdg.com';

/**
 * Почта DataHunter.
 *
 * У продукта свой домен и свой ящик: человек, нашедший на
 * datahunter.store адрес на чужом домене, либо решает, что попал не
 * туда, либо пишет не тем.
 */
export const DH_CONTACT_EMAIL = 'info@datahunter.store';

/**
 * Почта AiSearch.
 *
 * По той же причине, что и у DataHunter: у продукта свой домен, и адрес
 * на чужом домене в подвале читается как «я попал не туда».
 */
export const AIS_CONTACT_EMAIL = 'help@aisearch.tech';

/**
 * Почта русской витрины AiSearch.
 *
 * Тот же довод, что и у самого разделения доменов: человек на
 * aisearch.ru, увидев адрес на .tech, решает, что попал не туда, и
 * письма уходят в ящик другой команды. Отвечает по-русски другой ящик —
 * он и стоит на русском домене.
 */
export const AIS_RU_CONTACT_EMAIL = 'ai@aisearch.ru';
const DESCRIPTION =
  'ИИ-сервис для малого и среднего бизнеса: ответы клиентам, тексты, работа с документами. Без программистов, с поддержкой живых инженеров.';

/**
 * Бренд, о котором говорит разметка.
 *
 * Один образ обслуживает несколько сайтов, и без разделения datahunter.store
 * представлялся организацией INGDG со ссылкой на её домен: поисковик считал
 * бы продукт разделом чужого сайта.
 *
 * Бренд передаётся ветвью дерева, а не определяется по хосту запроса:
 * чтение заголовков в общем layout сделало бы динамическими сразу все
 * страницы, включая статические страницы основного сайта.
 */
export interface Brand {
  url: string;
  name: string;
  alternateName: string[];
  description: string;
  logo: string;
  /**
   * Адрес для связи, попадающий в разметку организации.
   *
   * Свойство бренда, а не общая константа: у DataHunter свой домен, и
   * писать в его разметке почту INGDG значит либо признавать продукт
   * разделом чужого сайта, либо отправлять человека писать не туда.
   */
  email: string;
  /**
   * Языки сайта и поддержки (#193).
   *
   * Тоже свойство бренда, а не общий список. Раньше во всю
   * разметку уезжали четыре языка основного сайта, и русская
   * витрина обещала ответ на испанском и сербском, хотя страницы там
   * только русские. Для человека это прямое враньё: напишет по-
   * сербски и ответа не получит, а для поисковика — заявка на
   * показ в выдаче, которая не подтверждается самими страницами.
   */
  yazyki: string[];
}

/**
 * Языки основного сайта и DataHunter: там страницы действительно
 * переведены на все четыре, и поддержка отвечает на них же.
 */
const YAZYKI_PLATFORMY = ['en', 'ru', 'es', 'sr'];

export const INGDG_BRAND: Brand = {
  url: SITE_URL,
  name: ORG_NAME,
  // Варианты написания бренда. Для короткой малоизвестной аббревиатуры это
  // важно: помогает поисковику связать разные написания с одной компанией
  // и не путать запросы вроде «ingdg» и «ing dg».
  alternateName: ['INGDG.com', 'ИНГДГ'],
  description: DESCRIPTION,
  logo: `${SITE_URL}/icons/icon-512x512.svg`,
  email: CONTACT_EMAIL,
  yazyki: YAZYKI_PLATFORMY,
};

export const DATAHUNTER_BRAND: Brand = {
  url: DH_SITE_URL,
  name: 'DataHunter',
  alternateName: ['DataHunter.store'],
  // Запасное значение на случай, если описание не передали: страницы
  // продукта берут его из перевода `dh_home.description` (#169).
  //
  // Английское, а не русское, и это принципиально. Прежняя строка была
  // русской, а передавать перевод забыли, и на /es и /sr в разметке
  // организации лежала кириллица: испанский поисковик читал описание
  // сайта на чужом языке. Умолчание обязано быть безопасным само по
  // себе, потому что именно оно всплывает, когда о нём забывают.
  //
  // Список инструментов здесь намеренно не перечисляется: прежнее
  // описание называло шесть из десяти, отстав на четыре инструмента, и
  // любая такая опись устаревает молча.
  description:
    'Free browser tools for working with data: editors, converters, encrypted notes and direct file transfer. No sign-up, nothing uploaded to a server.',
  logo: `${DH_SITE_URL}/icons/datahunter/icon-512x512.svg`,
  email: DH_CONTACT_EMAIL,
  yazyki: YAZYKI_PLATFORMY,
};

/**
 * Бренд AiSearch: свой на каждую витрину (#193).
 *
 * Функция, а не константа, потому что витрины две и они различаются
 * ровно тем, что попадает в разметку: адресом, почтой и языком. Одна
 * константа заставила бы выбирать, какую из витрин обмануть.
 *
 * Дефект, из-за которого это появилось: на страницах входа
 * aisearch.ru отдавалась `Organization` со значениями по умолчанию, то
 * есть INGDG — чужое имя, чужой домен ingdg.com, чужая почта
 * hello@ingdg.com. Для поисковика `Organization.url`, указывающий на
 * другой домен, чем сама страница, — противоречие: разметку либо
 * отбрасывают, либо она портит связку сайтов.
 *
 * Описание русское для русской витрины и английское для .tech.
 * Умолчание обязано быть безопасным само по себе: на DataHunter уже
 * наступали на русское описание, уехавшее в испанскую выдачу.
 */
export function AISEARCH_BRAND(ruDomen: boolean): Brand {
  const url = ruDomen ? AIS_RU_SITE_URL : AIS_SITE_URL;

  return {
    url,
    name: 'AiSearch',
    alternateName: ruDomen ? ['AiSearch.ru', 'ЭйАйСёрч'] : ['AiSearch.tech'],
    description: ruDomen
      ? 'Нейросети для текста, картинок и звука: написать текст, сделать таблицу, озвучить, расшифровать запись. Начать можно бесплатно.'
      : 'AI tools for text, images and audio: writing, tables from text, speech synthesis and transcription. Free to start.',
    // Логотип продукта, а не иконка платформы: по начертанию «ai» бренд
    // и узнаётся в выдаче.
    logo: `${url}/icons/aisearch/logo-115.png`,
    email: ruDomen ? AIS_RU_CONTACT_EMAIL : AIS_CONTACT_EMAIL,
    /*
      На русской витрине язык один, и это решено в её устройстве:
      адрес без языкового префикса, тексты под русские запросы,
      сама витрина собирается отдельной веткой (ais-ru-domen.ts).

      Международная витрина отвечает по-английски. Сербский и
      испанский здесь не перечислены: страниц на них нет, а
      обещать ответ на языке, которым мы не владеем, — то же
      самое, что выдуманный рейтинг в разметке.
    */
    yazyki: ruDomen ? ['ru'] : ['en'],
  };
}

function JsonLd({ data }: { data: Record<string, unknown> }) {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

/**
 * Организация, которой принадлежит домен.
 *
 * `description` можно передать переводом страницы: зашитая строка была
 * русской на всех языках (#108), и англоязычный поисковик читал кириллицу.
 * Тот же класс дефекта, что в twitter-тегах (#105) и манифесте (#120).
 *
 * Значение по умолчанию остаётся: у DataHunter и AiSearch свои описания,
 * и подставлять им перевод основного сайта нельзя.
 */
export function OrganizationSchema({
  brand: site = INGDG_BRAND,
  description,
}: { brand?: Brand; description?: string } = {}) {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${site.url}/#organization`,
        name: site.name,
        alternateName: site.alternateName,
        url: site.url,
        description: description ?? site.description,
        logo: {
          '@type': 'ImageObject',
          url: site.logo,
          width: 512,
          height: 512,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: site.email,
          contactType: 'customer support',
          availableLanguage: site.yazyki,
        },
      }}
    />
  );
}

/**
 * WebSite с поиском по сайту: даёт шанс на sitelinks searchbox в выдаче
 * и явно связывает домен с названием бренда.
 */
export function WebSiteSchema({
  brand: site = INGDG_BRAND,
  description,
}: { brand?: Brand; description?: string } = {}) {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${site.url}/#website`,
        name: site.name,
        alternateName: site.alternateName[0],
        url: site.url,
        description: description ?? site.description,
        inLanguage: site.yazyki,
        publisher: { '@id': `${site.url}/#organization` },
      }}
    />
  );
}

/**
 * Бесплатный инструмент INGDG: SoftwareApplication с нулевой ценой.
 *
 * Отдельно от WebApplicationSchema: та описывает весь сервис и по
 * исторической причине говорит по-русски, а здесь название и описание
 * берутся из перевода страницы.
 */
export function SoftwareToolSchema({
  name,
  description,
  path,
  locale,
}: {
  name: string;
  description: string;
  path: string;
  locale: string;
}) {
  const prefix = locale === 'en' ? '' : `/${locale}`;
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name,
        description,
        url: `${SITE_URL}${prefix}${path}`,
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        inLanguage: locale,
        isAccessibleForFree: true,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        publisher: { '@id': `${SITE_URL}/#organization` },
      }}
    />
  );
}

export function WebApplicationSchema() {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: ORG_NAME,
        url: SITE_URL,
        description: DESCRIPTION,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'All',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Бесплатный тариф без карты',
        },
      }}
    />
  );
}

export function FAQSchema({ items }: { items: { question: string; answer: string }[] }) {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }}
    />
  );
}

/**
 * Статья блога.
 *
 * `datePublished` и `dateModified` обязаны совпадать с датой, которую
 * видит человек на странице: расхождение Google замечает и трактует как
 * попытку выдать старый материал за свежий.
 *
 * `author` — организация, а не выдуманное имя. Подставлять несуществующего
 * автора ради «экспертности» нельзя: это тот же класс лжи, что фальшивые
 * отзывы, запрещённые в CONTENT_STRATEGY §6.2. Появится настоящий автор —
 * появится и Person.
 *
 * `inLanguage` ставится по локали статьи, а не по языку сайта: у нас один
 * материал существует не на всех языках, и пометить сербскую версию русским
 * языком означает предложить её не тем читателям.
 */
export function ArticleSchema({
  headline,
  description,
  url,
  image,
  published,
  modified,
  locale,
  base = SITE_URL,
  brand,
}: {
  headline: string;
  description: string;
  url: string;
  image: string;
  published: string;
  modified: string;
  locale: string;
  /**
   * Домен, чья организация выпустила статью.
   *
   * Раньше здесь стоял только `SITE_URL`, и статья блога DataHunter
   * называла бы автором организацию INGDG со ссылкой на чужой домен —
   * ровно то разделение брендов, ради которого заведён `Brand`.
   */
  base?: string;
  /**
   * Бренд целиком. Без него автор и издатель — только ссылка `@id` на
   * организацию, а она объявлена лишь на главной; поисковики не связывают
   * `@id` между страницами, и валидатор Google ругается на пустой
   * author.name (SEO-приёмка блога aisearch.ru, 02.10.2026).
   */
  brand?: Brand;
}) {
  const organizaciya = brand
    ? {
        '@type': 'Organization',
        '@id': `${base}/#organization`,
        name: brand.name,
        url: `${brand.url}/`,
        logo: { '@type': 'ImageObject', url: brand.logo },
      }
    : { '@id': `${base}/#organization` };
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline,
        description,
        image,
        inLanguage: locale,
        datePublished: published,
        dateModified: modified,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        author: organizaciya,
        publisher: organizaciya,
      }}
    />
  );
}

/**
 * Страница контактов.
 *
 * `contactPoint` с адресом почты объявлен в `Organization`, но на самой
 * странице контактов разметки не было (#107): поисковик видел страницу
 * как обычный текст и не связывал её с каналом связи компании.
 *
 * Языки поддержки берутся у бренда, как и в `Organization`, и это не
 * формальность: человек, пишущий по-сербски, должен знать, что ответят
 * ему на сербском, а не на английском. Обратное тоже верно — на витрине
 * с одним языком обещать четыре нельзя.
 *
 * Юридического лица и города здесь намеренно нет. Их можно было бы
 * добавить ради полноты разметки, но выдуманный адрес хуже отсутствующего:
 * это тот же класс лжи, что фальшивые отзывы из CONTENT_STRATEGY §6.2.
 * Появятся настоящие данные — появится и `address`.
 */
export function ContactPageSchema({
  name,
  description,
  url,
  email,
  brand: site = INGDG_BRAND,
}: {
  name: string;
  description: string;
  url: string;
  email: string;
  /** Бренд страницы: от него зависят языки и организация-владелец. */
  brand?: Brand;
}) {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name,
        description,
        url,
        mainEntity: {
          '@type': 'Organization',
          // Организация того же бренда, что и страница. Зашитый INGDG
          // связывал бы контакты продукта с чужой компанией.
          '@id': `${site.url}/#organization`,
          contactPoint: {
            '@type': 'ContactPoint',
            email,
            contactType: 'customer support',
            availableLanguage: site.yazyki,
          },
        },
      }}
    />
  );
}

/**
 * Хлебные крошки.
 *
 * `base` нужен по той же причине, что и в `ArticleSchema`: крошки
 * DataHunter указывали бы на страницы ingdg.com, то есть на адреса,
 * которых на этом домене нет.
 */
export function BreadcrumbSchema({
  items,
  base = SITE_URL,
}: {
  items: { name: string; url: string }[];
  base?: string;
}) {
  // Построитель общий с видимыми крошками (`seo/kroshki.tsx`): разметка
  // одна на весь монорепо, и правка формата не расходится по сайтам.
  return (
    <JsonLd
      data={razmetkaKroshek(
        items.map((i) => ({ name: i.name, href: i.url })),
        (href) => `${base}${href}`,
      )}
    />
  );
}

/** Тарифы для сниппетов с ценами. */
export function OfferCatalogSchema({
  offers,
}: {
  offers: { name: string; price: string; currency: string; description: string }[];
}) {
  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: ORG_NAME,
        description: DESCRIPTION,
        offers: offers.map((o) => ({
          '@type': 'Offer',
          name: o.name,
          price: o.price,
          priceCurrency: o.currency,
          description: o.description,
          url: `${SITE_URL}/pricing`,
        })),
      }}
    />
  );
}
